import getRoomAvailabilityStore from '../store/roomAvailabilityStore';
import type { RoomAvailabilityState } from '../store/schema/RoomAvailabilityState';
import getStore from '../store/store';
import loc from 'owa-localize';
import { mutator, mutatorAction } from 'satcheljs';
import {
    roomListSectionRecentRoomLists,
    roomListSectionCities,
} from '../components/RoomFilter/CalendarRoomListPicker.locstring.json';
import {
    updateRoomListsInStore,
    updateRoomsInStore,
    updateUserRoomListConfig,
    updateSearchStateInStore,
    updatePreferredRoomListsInStore,
    updateRoomAvailabilityResults,
    initializeRoomListPicker,
    updateTagsIndexInStore,
} from '../actions/calendarRoomActions';
import type { RoomList, Room } from '../components/RoomSelector/RoomSelector.types';
import type { ParentOption } from 'owa-calendar-composite-picker';
import { ObservableMap } from 'mobx';

mutator(updateUserRoomListConfig, actionMessage => {
    let viewState = getStore();
    if (viewState == null) {
        return;
    }

    let userCurrentRoomListName = actionMessage.userCurrentRoomListName;
    let userCurrentRoomListEmail = actionMessage.userCurrentRoomListEmail;

    if (viewState.roomListViewState.currentRoomList) {
        viewState.roomListViewState.currentRoomList.name = userCurrentRoomListName;
        viewState.roomListViewState.currentRoomList.email = userCurrentRoomListEmail;
    } else {
        viewState.roomListViewState.currentRoomList = {
            name: userCurrentRoomListName,
            email: userCurrentRoomListEmail,
        };
    }
});

mutator(updateRoomsInStore, actionMessage => {
    let viewState = getStore();
    if (viewState == null) {
        return;
    }
    let rooms = actionMessage.rooms;
    viewState.roomListViewState.rooms = rooms;
});

mutator(updateTagsIndexInStore, actionMessage => {
    const viewState = getStore();
    if (viewState == null) {
        return;
    }
    const rooms = actionMessage.rooms;
    const newTagsIndex = new ObservableMap<string, Room[]>();

    rooms.forEach(room => {
        room.tags?.forEach(tag => {
            if (tag.trim().length === 0) {
                return;
            }
            if (newTagsIndex.has(tag)) {
                newTagsIndex.get(tag).push(room);
            } else {
                newTagsIndex.set(tag, [room]);
            }
        });
    });

    viewState.roomListViewState.tagsIndex = newTagsIndex;
});

mutator(updateRoomListsInStore, actionMessage => {
    let viewState = getStore();
    if (viewState == null) {
        return;
    }
    let roomLists = actionMessage.roomLists;
    viewState.roomListViewState.roomLists = roomLists;
});

mutator(updatePreferredRoomListsInStore, actionMessage => {
    let viewState = getStore();
    if (viewState == null) {
        return;
    }
    let prl = actionMessage.preferredRoomLists;
    viewState.roomListViewState.preferredRoomLists = prl;
});

mutator(updateSearchStateInStore, actionMessage => {
    let viewState = getStore();
    if (viewState == null) {
        return;
    }
    let isSearching = actionMessage.isSearching;
    viewState.roomListViewState.isSearchingForRooms = isSearching;
});

mutator(updateRoomAvailabilityResults, actionMessage => {
    const {
        roomEmail,
        availability,
        roomRecurrenceAvailability,
        requestedCalendarTime,
        availabilityTimestamp,
    } = actionMessage;

    const roomAvailabilityStore = getRoomAvailabilityStore();
    if (roomAvailabilityStore == null) {
        return;
    }

    const roomAvailability: RoomAvailabilityState = {
        availability: availability,
        roomRecurrenceAvailability: roomRecurrenceAvailability,
        requestedCalendarTime: { ...requestedCalendarTime },
        availabilityTimestamp: availabilityTimestamp,
    };
    roomAvailabilityStore.roomAvailabilityStates.set(roomEmail, roomAvailability);
});

mutator(initializeRoomListPicker, ({ roomLists, citiesAndRoomLists }) => {
    const { roomListPickerViewState } = getStore();
    let currentIndex = roomLists.length;

    const options = [
        {
            account: loc(roomListSectionRecentRoomLists),
            isPrimaryAccount: true,
            leafOptions: (roomLists || []).map((roomList, index) => ({
                id: roomList.email,
                index: index,
                displayName: roomList.name,
                account: loc(roomListSectionRecentRoomLists),
                isShown: true,
            })),
            parentOptions: [],
        },
        {
            account: loc(roomListSectionCities),
            isPrimaryAccount: false,
            leafOptions: [],
            parentOptions: Object.keys(citiesAndRoomLists)
                .sort()
                .map(city => ({
                    id: city,
                    index: currentIndex++,
                    displayName: city,
                    account: loc(roomListSectionCities),
                    isShown: true,
                    isExpanded: false,
                    isFavorite: false,
                    children: citiesAndRoomLists[city].map(roomList => ({
                        id: roomList.email,
                        index: currentIndex++,
                        displayName: roomList.name,
                        account: loc(roomListSectionCities),
                        isShown: false,
                    })),
                })),
        },
    ];
    roomListPickerViewState.options = options;
    roomListPickerViewState.filteredOptions = options;
});

export const addChildrenInRoomListPicker = mutatorAction(
    'addChildrenInRoomListPicker',
    (roomLists: RoomList[], parentOption: ParentOption) => {
        parentOption.children = roomLists.map(roomList => ({
            id: roomList.email,
            index: 0,
            displayName: roomList.name,
            account: loc(roomListSectionCities),
            isShown: true,
        }));
    }
);

export const updateShowPlaceType = mutatorAction('updateShowPlaceType', showPlaceType => {
    const { roomListViewState } = getStore();
    roomListViewState.showPlaceType = showPlaceType;
});

export type { default as RoomType } from 'owa-service/lib/contract/RoomType';
export type { default as GetRoomListsResponse } from 'owa-service/lib/contract/GetRoomListsResponse';
